import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

// Role categories from CollaboratorOnboardingPage
const ROLE_CATEGORIES = {
    "Creative": ["Writer", "Director", "Showrunner", "Creative Producer"],
    "Production": ["Executive Producer", "Producer", "Line Producer", "Production Manager", "Post-Production Supervisor"],
    "Business & Distribution": ["Sales Agent", "Distribution Partner", "Business Affairs", "Talent Manager/Agent"],
    "Growth & Marketing": ["Social Media Manager", "Marketing/PR", "Audience Growth Strategist"],
    "Talent": ["On-Screen Talent / Host", "Composer / Musician"],
    "Other": ["Other"]
};

interface CollaboratorRoleSelectionProps {
  projectId: string;
  currentRoles?: string[];
  onRolesUpdated: (roles: string[]) => void;
  onCancel?: () => void;
}

export const CollaboratorRoleSelection = ({ 
  projectId, 
  currentRoles = [], 
  onRolesUpdated, 
  onCancel 
}: CollaboratorRoleSelectionProps) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>(currentRoles);
  const [otherRole, setOtherRole] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRoleToggle = (role: string) => {
    setSelectedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSubmit = async () => {
    if (selectedRoles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }

    // Process "Other" role if selected
    let finalRoles = [...selectedRoles];
    if (finalRoles.includes("Other") && otherRole.trim()) {
      finalRoles = finalRoles.filter(r => r !== "Other");
      finalRoles.push(`Other: ${otherRole.trim()}`);
    }

    setIsSubmitting(true);
    try {
      // Update collaborator roles via API
      await apiClient.updateCollaboratorRoles(projectId, finalRoles);
      toast.success("Roles updated successfully!");
      onRolesUpdated(finalRoles);
    } catch (error: any) {
      console.error("Failed to update roles:", error);
      toast.error(error.message || "Failed to update roles");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center justify-center p-4 font-sans text-[#111]">
      <div className="w-full max-w-[37.5rem]">
        <Card className="border-none shadow-lg shadow-gray-100/50">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-bold">Select Your Role</CardTitle>
            <CardDescription>
              Choose all roles that apply to your work on this project.
            </CardDescription>
          </CardHeader>
          <CardContent className="max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-6">
              {Object.entries(ROLE_CATEGORIES).map(([category, roles]) => (
                <div key={category}>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{category}</h3>
                  <div className="space-y-2">
                    {roles.map((role) => (
                      <div key={role} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer" onClick={(e) => {
                        // Prevent double triggering if they clicked the checkbox or label directly
                        if ((e.target as HTMLElement).tagName !== 'LABEL' && !(e.target as HTMLElement).closest('button')) {
                          handleRoleToggle(role);
                        }
                      }}>
                        <Checkbox
                          id={role}
                          checked={selectedRoles.includes(role)}
                          onCheckedChange={() => handleRoleToggle(role)}
                        />
                        <Label htmlFor={role} className="cursor-pointer font-normal text-base flex-1">{role}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {selectedRoles.includes("Other") && (
                <div className="pl-8 pt-0 animate-in fade-in slide-in-from-top-2">
                  <Input
                    placeholder="Please specify..."
                    value={otherRole}
                    onChange={(e) => setOtherRole(e.target.value)}
                    className="bg-gray-50/50"
                  />
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="pt-4 border-t flex gap-3">
            {onCancel && (
              <Button 
                variant="outline" 
                onClick={onCancel} 
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
            )}
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting || selectedRoles.length === 0}
              className="flex-1 h-11 text-base"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isSubmitting ? "Updating..." : "Update Roles"}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};